<template>
  <ClientOnly>
    <Teleport to=".filter-slide-in-target">
      <FocusTrap
        :active="filtersOpen"
        :escape-deactivates="true"
        :click-outside-deactivates="false"
      >
        <SlideIn name="OdoscopeFilterSlideIn">
          <template #slideInHeader="{ toggle: toggleSlot }">
            <div
              class="flex w-full items-center justify-between"
              :class="{ 'border-b border-b-gold px-4': isMobile }"
            >
              <Headline :is-uppercase="false" size="lg" tag="p"
                >{{ $t('filter.title') }}
              </Headline>
              <Button
                type="ghost"
                class="pr-0"
                :class="{ '-mr-2': isMobile }"
                @click="toggleSlot"
              >
                <template #icon>
                  <IconCloseS class="size-8" />
                </template>
              </Button>
            </div>
          </template>
          <template #slideInBody>
            <div class="border-b-8 border-b-gold sm:hidden">
              <OdoscopeSearchMobileSortingMenu :values="values" />
            </div>
            <div
              v-if="filterGroups.lined.length > 0"
              class="mx-4 mb-4 flex flex-row"
            >
              <template v-for="filter in filterGroups.lined" :key="filter.slug">
                <LazyRangeFilter
                  v-if="filter.type === 'range'"
                  :id="filter.name"
                  :filter="filter"
                  :min-value="filter.values[0].min"
                  :max-value="filter.values[0].max"
                  :model-value="
                    getPriceRange(filter.values[0].min, filter.values[0].max)
                  "
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  @update:model-value="
                    createPriceFilterHandler(
                      filter.values[0].min,
                      filter.values[0].max,
                    )($event)
                  "
                  @reset="resetPriceFilter"
                />

                <!-- TODO: Add other filters if they ever get implemented in OSC -->

                <AttributesFilter
                  v-else
                  :id="filter.name"
                  :filter="filter"
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  :model-value="
                    getFilterValuesForAppliedAttributeValues(
                      filter.slug,
                      localState[filter.slug],
                    )
                  "
                  @update:model-value-ids="
                    applyAttributeFilterToLocalState(filter.slug, $event)
                  "
                  @reset="resetFilter(filter.slug)"
                />
              </template>
            </div>
            <div class="flex flex-col" :class="{ 'gap-6': !isMobile }">
              <template
                v-for="filter in filterGroups.stacked"
                :key="filter.slug"
              >
                <LazyRangeFilter
                  v-if="filter.type === 'range'"
                  :id="filter.name"
                  :filter="filter"
                  :min-value="getUnfilteredRangeValues(filter)?.min"
                  :max-value="getUnfilteredRangeValues(filter)?.max"
                  :is-collapsible="filter.isCollapsible"
                  :model-value="
                    getPriceRange(
                      getUnfilteredRangeValues(filter)?.min,
                      getUnfilteredRangeValues(filter)?.max,
                    )
                  "
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  @update:model-value="
                    createPriceFilterHandler(
                      getUnfilteredRangeValues(filter)?.min,
                      getUnfilteredRangeValues(filter)?.max,
                    )($event)
                  "
                  @reset="resetPriceFilter"
                />

                <!-- TODO: Add other filters if they ever get implemented in OSC -->

                <AttributesFilter
                  v-else
                  :id="filter.name"
                  :filter="filter"
                  :is-collapsible="filter.isCollapsible"
                  :filter-values="getAvailableFilterValues(filter.slug)"
                  :model-value="
                    getFilterValuesForAppliedAttributeValues(
                      filter.slug,
                      localState[filter.slug],
                    )
                  "
                  @update:model-value-ids="
                    applyAttributeFilterToLocalState(filter.slug, $event)
                  "
                  @reset="resetFilter(filter.slug)"
                />
              </template>
            </div>
          </template>

          <template #slideInActions>
            <div
              class="flex flex-col sm:flex-row"
              :class="{ 'gap-4': !isMobile }"
            >
              <Button
                type="tertiary"
                :class="{ 'mx-4 mb-2 mt-4 bg-white': isMobile }"
                @click="resetAllFilters"
              >
                {{ $t('filter.delete') }}
              </Button>
              <Button
                :class="{ 'mx-4 mb-4 mt-2': isMobile }"
                data-test-id="apply-filter-button"
                :is-full-width="!isMobile"
                type="primary"
                @click="applyFilters(true)"
              >
                <!-- TODO: Check if the client wants to use the matchCount since we cannot calculate it accurately without firing requests to the OSC Search -->
                {{ matchCount.toString() + ' ' + $t('filter.show_result') }}
              </Button>
            </div>
          </template>
        </SlideIn>
      </FocusTrap>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
import type {
  CentAmount,
  ProductSearchQuery,
  RangeFilterItemWithValues,
} from '@scayle/storefront-nuxt'
import { getGroupedFilterableValues } from '@scayle/storefront-nuxt'
import { FocusTrap } from 'focus-trap-vue'
import type { OdoscopeFilter } from '@goldner-group/storefront-odoscope/types'
import type { RangeTuple } from '../ui/sliders/RangeSlider.vue'
import SlideIn from '#layers/base/components/layout/slideIns/SlideIn.vue'
import Headline from '#layers/base/components/ui/headlines/Headline.vue'
import useSlideIn from '#layers/base/composables/useSlideIn'

type CollapsibleFilter = OdoscopeFilter & { isCollapsible: boolean }

type Props = {
  filters: OdoscopeFilter[]
  matchCount: number
  appliedFilter: ProductSearchQuery
  values: OdoscopeSortValue[]
  unfilteredFilters?: OdoscopeFilter[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  applyFilters: [filters: any]
  resetFilters: []
}>()

const { filters: allAvailableFilters } = toRefs(props)

const route = useRoute()
const { isMobile } = useDeviceType()
const { toggle, isOpen: filtersOpen } = useSlideIn('OdoscopeFilterSlideIn')

// Adapt the route query to the local state format
// So for example, comma-separated values are converted to arrays
const adaptedRouteQuery = Object.fromEntries(
  Object.entries(route.query)
    .map(([key, value]) => {
      if (typeof value === 'string' && value?.indexOf(',') !== -1) {
        return [key, value.split(',')]
      } else if (value) {
        return [key, value?.toString()]
      }
      return [key, value]
    })
    .filter(Boolean),
) as Record<string, string | string[] | boolean>
const localState = ref(adaptedRouteQuery)
const justCleared = ref(false)

// Watch for sort parameter changes from mobile sorting and sync to local state
watch(
  () => route.query.sort,
  (newSort) => {
    if (newSort && newSort !== localState.value.sort) {
      localState.value.sort = newSort.toString()
    } else if (!newSort && localState.value.sort) {
      delete localState.value.sort
    }
  },
  { immediate: true },
)

const localStateAsRouteQuery = computed(() => {
  return Object.fromEntries(
    Object.entries(localState.value).map(([key, value]) => {
      return [key, Array.isArray(value) ? value.join(',') : value.toString()]
    }),
  )
})

// TODO: once we have all filters change these values to be Odoscope specific
const filtersPresentationOrder: Record<string, number> = {
  isNew: 0,
  sale: 1,
  colorRange: 2,
  prices: 3,
  sizeCategorySize: 4,
  size: 5,
}
const mobileExpandedFilters = ['isNew', 'sale']
const generalExpandedFilters = [
  ...mobileExpandedFilters,
  'colorRange',
  'prices',
  'sizeCategorySize',
  'size',
]

const getFilterValuesForAppliedAttributeValues = (
  slug: string,
  values?: string | string[] | boolean,
) => {
  const filter = availableFilterValues.value[slug]
  if (!filter || !values) {
    return []
  }

  const valuesArray = Array.isArray(values) ? values : [values]
  return filter.filter((v: any) => valuesArray.includes(v.value.toString()))
}

const isFilterCollapsible = (filterSlug: string) => {
  return isMobile.value
    ? !mobileExpandedFilters.includes(filterSlug)
    : !generalExpandedFilters.includes(filterSlug)
}

const getFilterPresentationPosition = (filterSlug: string) =>
  filtersPresentationOrder[filterSlug] ?? 100

const sortedFilters = computed(() => {
  return allAvailableFilters.value
    .map((filter) => {
      return { ...filter, isCollapsible: isFilterCollapsible(filter.slug) }
    })
    .sort(
      (a: CollapsibleFilter, b: CollapsibleFilter) =>
        getFilterPresentationPosition(a.slug) -
        getFilterPresentationPosition(b.slug),
    )
})

const filterGroups = computed(() => {
  return sortedFilters.value.reduce(
    (
      acc: { stacked: CollapsibleFilter[]; lined: CollapsibleFilter[] },
      f: CollapsibleFilter,
    ) => {
      if (isMobile.value && !f.isCollapsible) {
        acc.lined.push(f)
      } else {
        acc.stacked.push(f)
      }
      return acc
    },
    { stacked: [], lined: [] },
  )
})

const availableFilterValues = computed(() =>
  getGroupedFilterableValues(
    sortedFilters.value.map((f) => f.slug),
    sortedFilters.value,
  ),
)

const getAvailableFilterValues = (slug: string) =>
  availableFilterValues.value[slug] ?? []

const applyFilters = (toggleSlideIn: boolean = false) => {
  // if we're toggling it means that the filters are already applied with the current implementation
  // if the client does not want calls to the API everytime a filter is clicked on then remove this
  if (toggleSlideIn) {
    toggle()
  } else {
    emit('applyFilters', localStateAsRouteQuery.value)
  }
}

/**
 * @info Since OSC returns filtered prices, the range would be limited to already filtered values, which is why we need this function to get the unfiltered values.
 */
const getUnfilteredRangeValues = (filter: RangeFilterItemWithValues) => {
  if (!props.unfilteredFilters?.length) {
    return {
      min: filter.values[0].min,
      max: filter.values[0].max,
    }
  }

  const unfilteredRangeFilter = props.unfilteredFilters.find(
    (f) => f.slug === filter.slug && f.type === 'range',
  ) as RangeFilterItemWithValues

  if (unfilteredRangeFilter) {
    return {
      min: unfilteredRangeFilter.values[0].min,
      max: unfilteredRangeFilter.values[0].max,
    }
  } else {
    return {
      min: filter.values[0].min,
      max: filter.values[0].max,
    }
  }
}

const getPriceRange = (
  currentMin: CentAmount,
  currentMax: CentAmount,
): RangeTuple => {
  const minPrice = Array.isArray(localState.value.minPrice)
    ? localState.value.minPrice[0]
    : localState.value.minPrice
  const maxPrice = Array.isArray(localState.value.maxPrice)
    ? localState.value.maxPrice[0]
    : localState.value.maxPrice

  const appliedMin =
    minPrice && typeof minPrice === 'string' ? parseInt(minPrice) : currentMin
  const appliedMax =
    maxPrice && typeof maxPrice === 'string' ? parseInt(maxPrice) : currentMax

  const min = Math.max(currentMin, appliedMin ?? currentMin, 0)
  const max = Math.min(currentMax, appliedMax ?? currentMax)

  return [min, max]
}

const applyAttributeFilterToLocalState = (slug: string, values: string[]) => {
  if (Array.isArray(values)) {
    if (values.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete localState.value[slug]
    } else {
      localState.value[slug] = values
    }
  }

  // Apply filters immediately
  applyFilters()
}

const applyPriceFilterToLocalState = (
  prices: RangeTuple,
  currentMin?: CentAmount,
  currentMax?: CentAmount,
) => {
  // Skip applying price filters immediately after calling resetAllFilters to prevent default ranges to apply as filters
  if (justCleared.value) {
    return
  }

  // Don't apply price filters if they match the default range (no actual filtering)
  if (
    currentMin !== undefined &&
    currentMax !== undefined &&
    prices[0] === currentMin &&
    prices[1] === currentMax
  ) {
    // Remove any existing price filters since we're back to default
    delete localState.value.minPrice
    delete localState.value.maxPrice
    applyFilters()
    return
  }

  localState.value.minPrice = prices[0].toString()
  localState.value.maxPrice = prices[1].toString()

  // Apply filters immediately
  applyFilters()
}

const createPriceFilterHandler = (
  currentMin: CentAmount,
  currentMax: CentAmount,
) => {
  return (prices: RangeTuple) =>
    applyPriceFilterToLocalState(prices, currentMin, currentMax)
}

const resetPriceFilter = () => {
  delete localState.value.minPrice
  delete localState.value.maxPrice
}

const resetFilter = (slug: string) => {
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete localState.value[slug]
}

const resetAllFilters = () => {
  justCleared.value = true
  localState.value = {}
  emit('applyFilters', { sort: undefined })
  nextTick(() => {
    justCleared.value = false
  })
}

// const localAppliedBooleanValues = computed(
//   () => localAppliedFilters.value.appliedBooleanValues,
// )
</script>
